import React, { useState } from "react";
import { X, Eye, EyeOff, ShieldAlert } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../connector/api";

const ResetPasswordModal = ({ show, onClose, userId }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    // .trim() removes whitespace correctly in JS
    if (!password.trim()) {
      return toast.error("Password cannot be empty");
    }

    setLoading(true);
    setServerError("");
    try {
      await api.post(`/users/${userId}/reset-password/`, { password });
      toast.success("User password updated successfully!");
      setPassword("");
      onClose();
    } catch (err) {
      console.error(err);
      setServerError(err.response?.data?.detail || "Administrative override execution failed.");
      toast.error("Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-slate-950/40 dark:bg-zinc-950/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-in fade-in duration-150">
      <div className="bg-white dark:bg-[#121214] border border-slate-200/80 dark:border-zinc-800/80 rounded-xl shadow-2xl w-full max-w-sm overflow-hidden animate-in zoom-in-98 duration-200">
        
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-100 dark:border-zinc-800/60">
          <div>
            <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-zinc-500">Administrative Actions</h2>
            <h3 className="text-sm font-bold text-slate-900 dark:text-zinc-100 tracking-tight mt-0.5">Force Reset Password</h3>
          </div>
          <button 
            onClick={onClose} 
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-zinc-300 hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Container */}
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          
          {/* Input Block */}
          <div className="space-y-1">
            <label className="text-[10px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-wider block">
              New Override Password
            </label>
            <div className="relative rounded-xl">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (serverError) setServerError("");
                }}
                className="w-full bg-slate-50/60 dark:bg-zinc-900/40 border border-slate-200 dark:border-zinc-800 focus:border-transparent dark:focus:border-transparent focus:ring-2 focus:ring-zinc-950 dark:focus:ring-zinc-300 focus:bg-white dark:focus:bg-zinc-900 rounded-lg px-3 py-1.5 text-xs text-slate-800 dark:text-zinc-200 transition-all outline-none pr-9"
                required
              />
              <button
                type="button"
                tabIndex="-1"
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-400 dark:text-zinc-500 hover:text-slate-600 dark:hover:text-zinc-300 cursor-pointer"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </div>
            <p className="text-[10px] text-slate-400 dark:text-zinc-500 font-medium">
              Assign a temporary secret token. The user must authenticate using this value on their next access request cycle.
            </p>
          </div>

          {/* Exception Panel */}
          {serverError && (
            <div className="p-2.5 bg-rose-50 dark:bg-rose-950/10 border border-rose-100 dark:border-rose-900/30 rounded-lg text-rose-700 dark:text-rose-400 flex items-start gap-2 text-xs font-medium">
              <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{serverError}</span>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex items-center gap-2.5 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-200 dark:border-zinc-800 text-slate-500 dark:text-zinc-400 hover:text-slate-800 dark:hover:text-zinc-200 hover:bg-slate-50 dark:hover:bg-zinc-800/40 font-bold text-[10px] tracking-widest uppercase rounded-lg transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !password.trim()}
              className={`flex-1 px-4 py-2 text-white font-bold text-[10px] tracking-widest uppercase rounded-lg shadow-xs transition cursor-pointer ${
                loading || !password.trim()
                  ? "bg-[#002855] bg-opacity-80 dark:bg-zinc-800 text-slate-300 dark:text-zinc-600 border border-slate-200/50 dark:border-zinc-800/50 cursor-not-allowed shadow-none"
                  : "bg-[#002855] bg-opacity-80 hover:bg-opacity-95 dark:bg-zinc-200 dark:text-zinc-900 dark:hover:bg-white active:scale-[0.99]"
              }`}
            >
              {loading ? "Overriding..." : "Apply Reset"}
            </button>
          </div>
          
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordModal;